import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Alert } from "../../../components/ui/Alert";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Card } from "../../../components/ui/Card";
import { Modal } from "../../../components/ui/Modal";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../../../components/ui/State";
import { type Column, Table } from "../../../components/ui/Table";
import { ApiError } from "../../../lib/api";
import {
  type BootstrapToken,
  type BootstrapTokenDetail,
  type BootstrapTokenStatus,
  type IssuedBootstrapToken,
  bootstrapTokensApi,
} from "../../../lib/api/bootstrap";
import { formatDateTime } from "../../../lib/format";
import { t } from "../../../lib/i18n";

import { BootstrapJustIssuedCard } from "./BootstrapJustIssuedCard";
import { CreateBootstrapTokenForm } from "./CreateBootstrapTokenForm";

function statusTone(
  status: BootstrapTokenStatus,
): "success" | "danger" | "neutral" {
  if (status === "active") return "success";
  if (status === "revoked") return "danger";
  return "neutral";
}

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

export function BootstrapTokensPanel() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<BootstrapToken | null>(null);
  const [rotateTarget, setRotateTarget] = useState<BootstrapToken | null>(null);
  const [justIssued, setJustIssued] = useState<IssuedBootstrapToken | null>(null);
  const [viewTarget, setViewTarget] = useState<BootstrapTokenDetail | null>(null);

  const list = useQuery({
    queryKey: ["bootstrap", "tokens"],
    queryFn: bootstrapTokensApi.list,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["bootstrap", "tokens"] });
    void qc.invalidateQueries({ queryKey: ["company", "qr"] });
  };

  const viewQr = useMutation({
    mutationFn: (id: string) => bootstrapTokensApi.get(id),
    onSuccess: (token) => setViewTarget(token),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => bootstrapTokensApi.revoke(id),
    onSuccess: () => {
      invalidate();
      setRevokeTarget(null);
    },
  });

  const rotate = useMutation({
    mutationFn: (id: string) => bootstrapTokensApi.rotate(id),
    onSuccess: (issued) => {
      invalidate();
      setRotateTarget(null);
      setJustIssued(issued);
    },
  });

  const columns: Column<BootstrapToken>[] = [
    {
      key: "label",
      header: t("admin.bootstrap.list.column.label"),
      render: (row) => row.label,
    },
    {
      key: "validity_mode",
      header: t("admin.bootstrap.list.column.validityMode"),
      render: (row) => (
        <Badge tone="info">
          {t(`admin.bootstrap.validityMode.${row.validity_mode}`)}
        </Badge>
      ),
    },
    {
      key: "status",
      header: t("admin.bootstrap.list.column.status"),
      render: (row) => (
        <Badge tone={statusTone(row.status)}>
          {t(`admin.bootstrap.status.${row.status}`)}
        </Badge>
      ),
    },
    {
      key: "used_count",
      header: t("admin.bootstrap.list.column.usedCount"),
      render: (row) =>
        row.max_uses == null ? row.used_count : `${row.used_count} / ${row.max_uses}`,
      align: "right",
    },
    {
      key: "created_at",
      header: t("admin.bootstrap.list.column.createdAt"),
      render: (row) => formatDateTime(row.created_at),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {row.status === "active" && (
            <>
              {row.qr_available && (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={viewQr.isPending && viewQr.variables === row.id}
                  onClick={() => viewQr.mutate(row.id)}
                >
                  {t("admin.bootstrap.actions.viewQr")}
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setRotateTarget(row)}
              >
                {t("admin.bootstrap.actions.rotate")}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setRevokeTarget(row)}
              >
                {t("admin.bootstrap.actions.revoke")}
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ margin: 0 }}>{t("admin.bootstrap.title")}</h2>
          <p style={{ margin: "4px 0 0", color: "var(--text-muted, #666)" }}>
            {t("admin.bootstrap.subtitle")}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          {t("admin.bootstrap.create")}
        </Button>
      </div>

      {justIssued && <BootstrapJustIssuedCard issued={justIssued} />}

      <Card padding="none">
        {list.isLoading && <LoadingState />}
        {list.error && (
          <ErrorState
            titleKey="states.errorTitle"
            message={translateError(list.error)}
            action={
              <Button variant="secondary" size="sm" onClick={() => void list.refetch()}>
                {t("common.retry")}
              </Button>
            }
          />
        )}
        {list.data && (
          <Table
            rows={list.data}
            columns={columns}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                titleKey="admin.bootstrap.empty.title"
                bodyKey="admin.bootstrap.empty.body"
                action={
                  <Button onClick={() => setCreateOpen(true)}>
                    {t("admin.bootstrap.create")}
                  </Button>
                }
              />
            }
          />
        )}
      </Card>

      {viewQr.error && (
        <Alert tone="danger">{translateError(viewQr.error)}</Alert>
      )}

      <Modal
        open={viewTarget !== null}
        onClose={() => setViewTarget(null)}
        title={t("admin.bootstrap.view.title")}
      >
        {viewTarget?.qr_payload && (
          <BootstrapJustIssuedCard
            issued={{
              token: viewTarget,
              raw_code: viewTarget.raw_code,
              qr_payload: viewTarget.qr_payload,
            }}
          />
        )}
      </Modal>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t("admin.bootstrap.create.title")}
      >
        <CreateBootstrapTokenForm
          onCancel={() => setCreateOpen(false)}
          onCreated={(issued) => {
            setCreateOpen(false);
            invalidate();
            setJustIssued(issued);
          }}
        />
      </Modal>

      <Modal
        open={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        title={t("admin.bootstrap.revoke.title")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRevokeTarget(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="danger"
              loading={revoke.isPending}
              onClick={() => revokeTarget && revoke.mutate(revokeTarget.id)}
            >
              {t("admin.bootstrap.actions.revoke")}
            </Button>
          </>
        }
      >
        <p>{t("admin.bootstrap.revoke.body")}</p>
        {revokeTarget && (
          <div className="code-block" style={{ marginTop: 12 }}>
            {revokeTarget.label}
          </div>
        )}
        {revoke.error && (
          <div style={{ marginTop: 12 }}>
            <Alert tone="danger">{translateError(revoke.error)}</Alert>
          </div>
        )}
      </Modal>

      <Modal
        open={rotateTarget !== null}
        onClose={() => setRotateTarget(null)}
        title={t("admin.bootstrap.rotate.title")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRotateTarget(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              loading={rotate.isPending}
              onClick={() => rotateTarget && rotate.mutate(rotateTarget.id)}
            >
              {t("admin.bootstrap.actions.rotate")}
            </Button>
          </>
        }
      >
        <p>{t("admin.bootstrap.rotate.body")}</p>
        {rotateTarget && (
          <div className="code-block" style={{ marginTop: 12 }}>
            {rotateTarget.label}
          </div>
        )}
        {rotate.error && (
          <div style={{ marginTop: 12 }}>
            <Alert tone="danger">{translateError(rotate.error)}</Alert>
          </div>
        )}
      </Modal>
    </div>
  );
}
